import useClassNames from 'helpers/hooks/useClassNames';
import { ButtonProps, ButtonSize, ButtonVariant } from '.';

type variantBasedClassName = { [key in ButtonVariant]?: string };
type IncludesIcon = { includesIcon: boolean };
type UseButtonClassNames = (
  props: Pick<ButtonProps, 'variant' | 'size' | 'className' | 'loading' | 'asSkeleton'> & IncludesIcon,
) => string;

const useButtonClassNames: UseButtonClassNames = ({
  variant,
  size,
  className = '',
  includesIcon,
  loading,
  asSkeleton = false,
}) => {
  const sizeClassNames: { [key in ButtonSize]: string } = {
    xs: 'p-8 !text-sm',
    s: 'py-12 px-24',
    m: 'py-12 px-36',
    l: 'py-12 px-48',
    fit: 'p-0',
    full: 'py-12 w-full',
    icon: 'w-40 h-40 grid items-center justify-center',
  };

  const primaryClassName = useClassNames([
    'bg-cta-fill-primary text-inverted border border-2 border-cta-fill-primary',
    'hover:border-cta-fill-primary-hover hover:bg-cta-fill-primary-hover',
    'active:bg-cta-fill-primary-pressed active:border active:border-2 active:border-cta-fill-primary',
    'focus:outline-none focus-visible:bg-cta-fill-primary focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-2 focus-visible:outline-cta-fill-primary',
    {
      'disabled:bg-cta-fill-primary-disabled disabled:border-cta-fill-primary-disabled disabled:text-inverted':
        !loading,
    },
    {
      'border-cta-fill-primary-pressed': !!loading,
    },
  ]);

  const secondaryClassName = useClassNames([
    'bg-transparent text-cta-fill-primary border border-2 border-cta-fill-primary',
    'hover:text-cta-fill-primary-hover hover:border hover:border-2 hover:border-cta-fill-primary-hover',
    'active:text-cta-fill-primary-pressed active:border active:border-2 active:border-cta-fill-primary-pressed ',
    'focus-visible:text-cta-fill-primary focus-visible:border focus-visible:border-2 focus-visible:border-cta-fill-primary focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-2 focus-visible:outline-cta-fill-primary',
    {
      'disabled:text-cta-stroke-primary-disabled disabled:border-cta-stroke-primary-disabled': !loading,
    },
    {
      'disabled:bg-transparent border-cta-fill-primary-pressed': !!loading,
    },
  ]);

  const secondaryInvertedClassName = useClassNames([
    'bg-transparent text-cta-stroke-primary border border-2 border-cta-stroke-primary',
    'hover:text-cta-stroke-primary hover:border-cta-stroke-primary',
    'active:text-cta-stroke-primary active:border-cta-stroke-primary',
    'focus-visible:border-cta-stroke-primary focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-2 focus-visible:outline-cta-stroke-primary',
    {
      'disabled:bg-cta-stroke-primary-disabled disabled:text-cta-stroke-primary disabled:!border-cta-stroke-primary-disabled':
        !loading,
    },
    {
      'disabled:border-cta-stroke-primary disabled:bg-transparent disabled:': !!loading,
    },
  ]);

  const secondaryBlack = useClassNames([
    'bg-neutral-black-700 text-white border border-2 border-neutral-black-700',
    'hover:bg-neutral-black-400 hover:border-neutral-black-400 hover:text-white',
    'active:bg-neutral-black-400 active:border-neutral-black-400 active:text-white',
    'focus-visible:border-neutral-black-700 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-2 focus-visible:outline-neutral-black-700',
    {
      'disabled:bg-neutral-black-200 disabled:text-white disabled:!border-neutral-black-200':
        !loading,
    },
    {
      'disabled:border-neutral-black-200 disabled:bg-transparent': !!loading,
    },
  ]);

  const ghostClassName = 'disabled:text-neutral-400';

  const warningClassName = useClassNames([
    'bg-red-500 text-neutral-100 outline-offset-0',
    'hover:bg-red-600',
    'active:shadow-button active:disabled:shadow-none active:bg-red-500',
    'focus-visible:outline focus-visible:bg-red-700 !focus-visible:outline-gray-700 focus-visible:border focus-visible:outline-offset-[3px]',
    'disabled:bg-neutral-400 disabled:text-neutral-100',
  ]);

  const variantClassNames: variantBasedClassName = {
    primary: primaryClassName,
    secondary: secondaryClassName,
    ghost: ghostClassName,
    'secondary-inverted': secondaryInvertedClassName,
    warning: warningClassName,
    'secondary-black': secondaryBlack
  };

  const buttonClassName = useClassNames([
    sizeClassNames[size as ButtonSize],
    { 'flex gap-10 items-center justify-center px-24': includesIcon },
    { 'flex gap-10 items-center justify-center px-8': includesIcon && size === 'xs' },
    { 'cursor-not-allowed': !!loading },
    'relative text-base leading-4 transition overflow-hidden tracking-wide font-bold',
    asSkeleton ? 'relative' : (variantClassNames[variant as ButtonVariant] as string),
    className,
  ]);

  return buttonClassName;
};

export default useButtonClassNames;
